import { tenantQuery } from 'helpers/filter/TenantFilter'
import { activityTrackingQuery } from 'helpers/query/ActivityTrackingQuery'
import { incidencesQuery } from 'helpers/query/IncidencesQuery'
import _ from 'lodash'
import { mockActivityTrackingResponse } from 'mock/activity-tracking-mock'
import { mockIncidencesResponse, mockIncidenceUpdateResponse } from 'mock/incidences-mock'
import { mockUserResponse } from 'mock/user-mock'
import { Incidences, UpdateIncidenceParams } from 'models/incidences'
import moment from 'moment'
import configProvider from '../../config'
import { addQueryString } from '../../helpers/url'
import { log } from '../../logger'
import { mockBilling } from '../../mock/billing'
import {
  mockTenantById,
  mockTenantContact,
  mockTenants,
  mockTenantsPermission,
  mockTenantsResponse,
  mockWaitListGetResponse,
  mockWaitListQueueResponse,
  mockWaitListSettingResponse,
  updateTenantBasedOnPayload,
} from '../../mock/tenant-mock'
import { Billing } from '../../models/billing'
import { ActivityTrackingFilter, IncidencesFilter, TenantFilter } from '../../models/filter'
import { ActivityTrackingResponse, IncidencesResponse, TenantResponse } from '../../models/http'
import {
  ChargerSetting,
  ChargerTroubleShootPermission,
  Tenant,
  TenantContactInfo,
  TenantDeployment,
  TenantPartner,
  TenantPayload,
  TenantRef,
  TenantUniformPricing,
  WaitListQueueResponse,
  WaitListSettingPayload,
  WaitListSettingResponse,
} from '../../models/tenant'
import { AugmentedUser, toAugmentedUser, User, UserRef } from '../../models/user'
import CaseConverter from './case-converter'
import { apiClient } from './client'
import { mockTenantsRef, returnMock } from './mock'
import { mockServiceDescriptions, mockServiceStatusReasons } from 'mock/listing-mock'

export async function GetTenants(isMockUpEnabled: boolean, filter: TenantFilter): Promise<TenantResponse> {
  let url = '/tenants'

  const queryParams = tenantQuery(filter)

  url = addQueryString(url, queryParams)

  log('fetching tenants URL', { url })
  if (isMockUpEnabled) {
    return returnMock(mockTenantsResponse(filter))
  }
  return apiClient(url, {})
}

export async function GetTenant(tenantId: number, isMockUpEnabled: boolean): Promise<Tenant> {
  let url = `/tenants/${tenantId}`

  log('fetching tenant', { url })
  if (isMockUpEnabled) {
    return Promise.resolve(mockTenantById[tenantId])
  }
  return apiClient(url, {})
}

export async function CreateTenant(tenant: Tenant): Promise<Tenant> {
  let url = `/tenants`

  log('creating tenant', { url: url })
  if (configProvider.config.mockEnabled) {
    return Promise.resolve(mockTenants[0])
  }

  const snakeCasedTenantModel = CaseConverter.camelToSnakeCase(tenant)
  const newKeys = { location: 'location_attributes', weekly_schedules: 'weekly_schedules_attributes' }
  const updatedTenantModel = CaseConverter.renameKeys(snakeCasedTenantModel, newKeys)
  delete updatedTenantModel.id

  return apiClient<Tenant>(url, {
    method: 'POST',
    body: JSON.stringify(updatedTenantModel),
  })
}

export async function ModifyTenantPartners(tenantId: string, partners: TenantPartner[]): Promise<Tenant> {
  let url = `/tenants/${tenantId}`

  log('adding and/or removing partners to/from tenant', { url })

  const foundTenant: Tenant = { ...mockTenantById[Number(tenantId)] }

  const removePartnerName = (partners: any) => {
    return partners.tenant_partners.map((partner: any) => {
      const { partnerName, ...rest } = partner
      return rest
    })
  }

  const backendPayload = {
    tenant: {
      tenant_partners: removePartnerName(partners),
    },
  }

  const snakeCasedTenantModel = CaseConverter.camelToSnakeCase(backendPayload)

  if (configProvider.config.mockEnabled) {
    return Promise.resolve(updateTenantBasedOnPayload(foundTenant))
  }

  return apiClient<Tenant>(url, {
    method: 'PUT',
    body: JSON.stringify(snakeCasedTenantModel),
  })
}

export async function UpdateTenant(tenant: TenantPayload): Promise<Tenant> {
  let url = `/tenants/${tenant.id}`
  log('updating tenant', { url, tenant })

  let updatedTenant: any = { ...tenant }

  updatedTenant = {
    ...updatedTenant,
    revSharePartnerTaggingId: tenant?.revSharePartner === null ? null : tenant?.revSharePartner?.id,
    revSharePartner: undefined,
  }

  // modify the format and remove partner name from tenant_partners
  if (tenant && tenant.tenant_partners) {
    delete updatedTenant.tenant_partners
    updatedTenant = {
      ...updatedTenant,
      tenant: {
        tenant_partners: tenant.tenant_partners.map((partner) => {
          return {
            partner_id: partner.partnerId,
            integration_identifier: partner.integrationIdentifier,
            is_integration_identifier_partner_provided: partner.isIntegrationIdentifierPartnerProvided
          }
        }),
      },
    }
  }

  const snakeCasedTenantModel = CaseConverter.camelToSnakeCase(updatedTenant)
  const newKeys = {
    location: 'location_attributes',
    weekly_schedules: 'weekly_schedules_attributes',
    tenant_contacts: 'tenant_contacts_attributes',
  }
  const updatedTenantModel = CaseConverter.renameKeys(snakeCasedTenantModel, newKeys)
  delete updatedTenantModel.id

  delete updatedTenantModel.updated_at
  //partner_ids need to be an array for backend to accept - didnt change camelToSnakeCase to not affect other values
  if (tenant.partnerIds) {
    updatedTenantModel.partner_ids = tenant.partnerIds
  }

  if (updatedTenantModel.charger_setting) {
    updatedTenantModel.charger_setting = `${updatedTenantModel.charger_setting.map((t: ChargerSetting) => t)}`
  }

  log('updatedTenantModel', updatedTenantModel)

  if (configProvider.config.mockEnabled) {
    return Promise.resolve(updateTenantBasedOnPayload(tenant))
  }

  return apiClient<Tenant>(url, {
    method: 'PUT',
    body: JSON.stringify(updatedTenantModel),
  })
}

export async function AuthorizeUser(tenantIds: string[], user: UserRef): Promise<AugmentedUser[]> {
  let url = `/tenants/authorize`

  log('fetching tenant', { url: url })
  if (configProvider.config.mockEnabled) {
    if (configProvider.config.mockEnabled) {
      return Promise.resolve(mockUserResponse()['data'].map((u) => toAugmentedUser(u)))
    }
  }

  return apiClient<User[]>(url, {
    method: 'POST',
    body: JSON.stringify({
      tenant_id: `${tenantIds.join(',')}`,
      user_id: user.id,
    }),
  }).then((users) => users.map((u) => toAugmentedUser(u)))
}

export async function GetTenantContacts(tenantId: string): Promise<TenantContactInfo[]> {
  let url = `/tenants/${tenantId}/tenant_contacts`

  log('updating tenant', { url: url })
  if (configProvider.config.mockEnabled) {
    return Promise.resolve(mockTenantContact)
  }

  return apiClient(url, {})
}

export async function UpdateTenantContacts(tenantId: string, tenantContactInfo: TenantContactInfo[]): Promise<Tenant> {
  const url = `/tenants/${tenantId}/tenant_contacts`

  const snakeCasedTenantModel = tenantContactInfo.map((contact) => {
    return CaseConverter.camelToSnakeCase(contact)
  })

  log('updating tenant contacts', { url: url })
  if (configProvider.config.mockEnabled) {
    return Promise.resolve(mockTenants[0])
  }

  return apiClient<Tenant>(url, {
    method: 'POST',
    body: JSON.stringify({
      tenant_contact: [...snakeCasedTenantModel],
    }),
  })
}

export async function UpdateTenantDeployment(tenantId: string, tenantDeployment: TenantDeployment): Promise<Tenant> {
  let url = `/tenants/${tenantId}/tenant_deployment`

  log('updating tenant', { url: url })
  if (configProvider.config.mockEnabled) {
    return Promise.resolve(mockTenants[0])
  }

  const snakeCasedTenantModel = CaseConverter.camelToSnakeCase(tenantDeployment)

  if (tenantDeployment.parkingStalls) {
    snakeCasedTenantModel.parking_stalls = [...Object.values(tenantDeployment.parkingStalls)]
  }
  if (tenantDeployment.loadManagementConfiguration) {
    snakeCasedTenantModel.load_management_configuration = [
      ...Object.values(tenantDeployment.loadManagementConfiguration!),
    ]
  }
  if (tenantDeployment.remoteDesktopIds) {
    snakeCasedTenantModel.remote_desktop_ids = [...Object.values(tenantDeployment.remoteDesktopIds)]
  }

  return apiClient<Tenant>(url, {
    method: 'POST',
    body: JSON.stringify({ tenant_deployment: { ...snakeCasedTenantModel } }),
  })
}

export async function UpdateTenantUniformPricing(
  tenantId: string,
  tenantUniformPricing: TenantUniformPricing,
): Promise<Tenant> {
  let url = `/tenants/${tenantId}/tenant_uniform_pricing`

  log('updating tenant uniform pricing', { url: url })

  if (configProvider.config.mockEnabled) {
    return Promise.resolve(mockTenants[0])
  }

  // remove any null or undefined properties
  const cleandTenantUniformPricingObj = _.omitBy(tenantUniformPricing, _.isNil)

  const snakeCasedUniformPricingModel = CaseConverter.camelToSnakeCase(cleandTenantUniformPricingObj)
  if (snakeCasedUniformPricingModel.hasOwnProperty('tenant_listing_authors')) {
    delete snakeCasedUniformPricingModel.tenant_listing_authors
  }
  if (snakeCasedUniformPricingModel.hasOwnProperty('tenant_listing_stripe_accounts')) {
    delete snakeCasedUniformPricingModel.tenant_listing_stripe_accounts
  }

  snakeCasedUniformPricingModel.time_of_day_price_weekday = [
    ...Object.values(tenantUniformPricing.timeOfDayPriceWeekday),
  ].map((num) => {
    return Number((num * 100).toFixed(2))
  })
  snakeCasedUniformPricingModel.time_of_day_price_weekend = [
    ...Object.values(tenantUniformPricing.timeOfDayPriceWeekend),
  ].map((num) => {
    return Number((num * 100).toFixed(2))
  })

  return apiClient<Tenant>(url, {
    method: 'POST',
    body: JSON.stringify({ tenant_uniform_pricing: { ...snakeCasedUniformPricingModel } }),
  })
}

export async function FindTenants(
  currentUser: User | undefined,
  location: boolean,
  isMockUpEnabled: boolean,
  term?: string,
): Promise<TenantRef[]> {
  let url = '/tenants/find'

  let queryParams: { [key: string]: string | boolean } = {}

  queryParams['tenant_location'] = location

  if (term) {
    queryParams['term'] = `${term}`
  }
  url = addQueryString(url, queryParams)

  log('fetching tenants', { url })

  let tenants: TenantRef[]
  if (isMockUpEnabled) {
    tenants = await Promise.resolve(mockTenantsRef)
  } else {
    tenants = await apiClient(url, {})
  }

  // Return a new Promise with the modified data
  return Promise.resolve(
    tenants.map((tenant) => ({
      ...tenant,
      combineName: currentUser?.role === 'admin' ? tenant.combineName : tenant.displayName ?? tenant.name,
    })),
  )
}

export async function GetTenantBilling(tenantId: string): Promise<Billing> {
  const url = `/tenants/${tenantId}/billing`
  log('Fetching tenant billing', { url })
  if (configProvider.config.mockEnabled) {
    return Promise.resolve(mockBilling)
  }
  return apiClient(url, {})
}

export async function UpdateTenantBilling(tenantId: string, billing: Billing): Promise<Billing> {
  const url = `/tenants/${tenantId}/update_billing`
  log('update tenant billing info', { url })
  if (configProvider.config.mockEnabled) {
    return Promise.resolve(mockBilling)
  }
  return apiClient(url, {
    method: 'PUT',
    body: JSON.stringify({
      billing: {
        tenant_id: tenantId,
        contact_name: billing.contactName,
        email: billing.email,
        phone: billing.phone,
        notes: billing.notes,
        remittance_type_id: billing.remittanceType?.id || null,
      },
    }),
  })
}

export async function GetChargerTroubleShootPermission(tenantId: string): Promise<ChargerTroubleShootPermission> {
  const url = `/tenants/${tenantId}/permissions`
  log('Fetching tenant permissions', { url })
  if (configProvider.config.mockEnabled) {
    return Promise.resolve(mockTenantsPermission)
  }
  return apiClient(url, {})
}

export async function GetTenantActivityTracking(
  tenantId: string,
  startDate: moment.Moment,
  endDate: moment.Moment,
  filter: ActivityTrackingFilter,
): Promise<ActivityTrackingResponse> {
  let url = `/tenants/${tenantId}/activity_tracking`

  const queryParams = activityTrackingQuery(filter, startDate, endDate)

  url = addQueryString(url, queryParams)

  log('Fetching tenant activity tracking', { url })
  if (configProvider.config.mockEnabled) {
    return returnMock(mockActivityTrackingResponse(startDate, endDate, filter))
  }
  return apiClient(url, {})
}

export async function GetTenantIncidences(
  tenantId: string,
  startDate: moment.Moment,
  endDate: moment.Moment,
  filter: IncidencesFilter,
  isMockUpEnabled: boolean,
): Promise<IncidencesResponse> {
  let url = `/service_status_tracking_events`

  const queryParams = {
    ...incidencesQuery(filter, startDate, endDate),
    tenant_id: tenantId,
  }

  url = addQueryString(url, queryParams)

  log('Fetching tenant incidences', { url })
  if (isMockUpEnabled) {
    // Filter mock data based on startDate, endDate, and filter
    let filteredMockData = mockIncidencesResponse.data.filter((incidence) => {
      const incidenceStartTime = moment(incidence.startTime)
      const incidenceEndTime = moment(incidence.endTime)

      // Check if the incidence falls within the date range
      const isInDateRange =
        (incidenceStartTime.isSameOrAfter(startDate, 'day') && incidenceStartTime.isSameOrBefore(endDate, 'day')) ||
        (incidenceEndTime.isSameOrAfter(startDate, 'day') && incidenceEndTime.isSameOrBefore(endDate, 'day')) ||
        (incidenceStartTime.isBefore(startDate, 'day') && incidenceEndTime.isAfter(endDate, 'day'))

      if (!isInDateRange) return false

      // Filter by listings
      if (filter.listings && filter.listings.length > 0) {
        const listingTitles = filter.listings.map((listing) => listing.title)
        if (!listingTitles.includes(incidence.listingTitle)) {
          return false
        }
      }

      // Filter by zendesk ID
      if (filter.zendeskId && filter.zendeskId.trim() !== '') {
        if (!incidence.zendeskId || !incidence.zendeskId.includes(filter.zendeskId)) {
          return false
        }
      }

      // Filter by downtime
      if (filter.downtime && filter.downtime.trim() !== '') {
        if (incidence.downtime !== filter.downtime) {
          return false
        }
      }
      // Filter by root cause IDs - map IDs to names from mock data
      if (filter.rootCauseIds && filter.rootCauseIds.length > 0) {
        const mockReasons = mockServiceStatusReasons().data
        const selectedRootCauseNames = mockReasons
          .filter((reason) => filter.rootCauseIds!.includes(reason.id))
          .map((reason) => reason.name)

        if (!selectedRootCauseNames.includes(incidence.rootCause || '')) {
          return false
        }
      }

      // Filter by resolution IDs - map IDs to names from mock data
      if (filter.resolutionIds && filter.resolutionIds.length > 0) {
        const mockResolutions = mockServiceDescriptions

        const selectedResolutionNames = mockResolutions
          .filter((resolution) => filter.resolutionIds!.includes(resolution.id))
          .map((resolution) => resolution.name)

        if (!selectedResolutionNames.includes(incidence.resolution || '')) {
          return false
        }
      }

      return true
    })

    // Apply pagination
    const page = filter.page || 1
    const perPage = 20
    const startIndex = (page - 1) * perPage
    const endIndex = startIndex + perPage
    const paginatedData = filteredMockData.slice(startIndex, endIndex)

    return returnMock({
      data: paginatedData,
      pagination: {
        currentPage: page,
        totalEntries: filteredMockData.length,
        perPage: perPage,
      },
    })
  }
  return apiClient(url, {})
}

export async function UpdateTenantIncidences(
  tenantId: string,
  incidenceId: number,
  props: UpdateIncidenceParams,
): Promise<Incidences> {
  let url = `/service_status_tracking_events/${incidenceId}`

  log('Updating tenant incidences', { url })
  if (configProvider.config.mockEnabled) {
    return returnMock(mockIncidenceUpdateResponse)
  }
  return apiClient(url, {
    method: 'PATCH',
    body: JSON.stringify({
      ...props,
      start_time: props.start_time ? moment(props.start_time).utc().toISOString() : undefined,
      end_time: props.end_time ? moment(props.end_time).utc().toISOString() : undefined,
      tenant_id: tenantId,
    }),
  })
}

export async function GetWaitListDetails(tenantId: number, isMockUpEnabled: boolean): Promise<WaitListSettingResponse> {
  let url = `/tenants/${tenantId}/waitlist_setting`

  log('fetching tenant waitlist details', { url })
  if (isMockUpEnabled) {
    return Promise.resolve(mockWaitListGetResponse)
  }
  return apiClient(url, {})
}

export async function GetQueueDetails(tenantId: number, isMockUpEnabled: boolean): Promise<WaitListQueueResponse> {
  let url = `/tenants/${tenantId}/waitlist_queue`

  log('fetching tenant queue details', { url })
  if (isMockUpEnabled) {
    return Promise.resolve(mockWaitListQueueResponse)
  }
  return apiClient(url, {})
}

export async function CreateWaitListDetails(
  tenantId: number,
  waitListDetails: WaitListSettingPayload,
  isMockUpEnabled: boolean,
): Promise<WaitListSettingResponse> {
  let url = `/tenants/${tenantId}/waitlist_setting`

  log('creating tenant', { url: url })
  if (isMockUpEnabled) {
    return Promise.resolve(mockWaitListSettingResponse)
  }

  return apiClient(url, {
    method: 'POST',
    body: JSON.stringify(waitListDetails),
  })
}

export async function ModifyWaitListDetails(
  tenantId: number,
  waitListDetails: WaitListSettingPayload,
  isMockUpEnabled: boolean,
): Promise<WaitListSettingResponse> {
  let url = `/tenants/${tenantId}/waitlist_setting`

  log('modify waitlist details', { url })

  if (isMockUpEnabled) {
    return Promise.resolve(mockWaitListSettingResponse)
  }

  return apiClient(url, {
    method: 'PUT',
    body: JSON.stringify(waitListDetails),
  })
}
